import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type React from 'react';

export type ImageModalV2SurfaceLifecycleOptions = {
    /** Whether the surface should currently be shown. Toggling this to `false` starts the exit sequence. */
    isOpen: boolean;
    /** Milliseconds to wait for a matching `transitionend` before forcing exit completion. */
    fallbackMs: number;
    /** Extra hold applied before `onExitComplete` fires once the exit has settled. Defaults to no hold. */
    exitHandoffMs?: number;
    /** `transitionend` `propertyName` that marks the exit transition as finished. Defaults to `'transform'`. */
    transitionProperty?: string;
    /** When true, only `transitionend` events whose target is the bound element itself count. */
    requireOwnTarget?: boolean;
    /** Render already-shown on mount instead of animating in, when `isOpen` starts `true`. */
    skipInitialEnter?: boolean;
    /** Skip the reveal/exit machinery entirely for a surface that has never been opened. */
    guardNeverOpened?: boolean;
    /** Called once the exit sequence (transition or fallback, plus any handoff hold) has completed. */
    onExitComplete?: () => void;
    /** Called once the enter transition or its fallback completes. */
    onEnterComplete?: () => void;
};

export type ImageModalV2SurfaceLifecycle = {
    /** True once the surface has been revealed (after the portal-ready frame wait); false while entering or exited. */
    isShown: boolean;
    /** Latches `true` the first time the surface enters, and stays `true` afterwards, including during exit. */
    hasEntered: boolean;
    /** Attach to the animated element's `onTransitionEnd`. */
    handleTransitionEnd: (event: React.TransitionEvent<Element>) => void;
};

/**
 * Shared reveal/exit state machine for modal-local surfaces (toasts, toolbar bands) that animate in
 * and out under direct CSS control rather than through a transition-group library.
 *
 * Entering: on `isOpen` becoming `true`, waits three animation frames. The extra frame lets a
 * portal-backed toast commit its closed state before it changes, guaranteeing a transition rather than
 * a jump-cut. It then reveals the surface (`isShown`) and latches `hasEntered`.
 *
 * Exiting: on `isOpen` becoming `false`, waits the same three frames, then marks the surface hidden and
 * arms a fallback timer. The exit is considered complete on whichever happens first: a `transitionend`
 * matching `transitionProperty` (and `requireOwnTarget`, if set), or the fallback timer expiring after
 * `fallbackMs`. Completion is guarded so it only fires once per exit, then — optionally after
 * `exitHandoffMs` — calls `onExitComplete`.
 */
export const useImageModalV2SurfaceLifecycle = ({
    isOpen,
    fallbackMs,
    exitHandoffMs = 0,
    transitionProperty = 'transform',
    requireOwnTarget = false,
    skipInitialEnter = false,
    guardNeverOpened = false,
    onExitComplete,
    onEnterComplete,
}: ImageModalV2SurfaceLifecycleOptions): ImageModalV2SurfaceLifecycle => {
    const [isShown, setIsShown] = useState(() => isOpen && skipInitialEnter);
    const [hasEntered, setHasEntered] = useState(() => isOpen && skipInitialEnter);
    const hasBeenOpenRef = useRef(isOpen);
    const exitCompletedRef = useRef(true);
    const enterCompletedRef = useRef(!isOpen || skipInitialEnter);
    const exitHandoffTimeoutRef = useRef(0);
    const onExitCompleteRef = useRef(onExitComplete);
    const onEnterCompleteRef = useRef(onEnterComplete);
    onExitCompleteRef.current = onExitComplete;
    onEnterCompleteRef.current = onEnterComplete;

    const completeExit = useCallback(() => {
        if (exitCompletedRef.current) return;
        exitCompletedRef.current = true;

        if (exitHandoffMs > 0) {
            exitHandoffTimeoutRef.current = window.setTimeout(() => onExitCompleteRef.current?.(), exitHandoffMs);
            return;
        }

        onExitCompleteRef.current?.();
    }, [exitHandoffMs]);

    const completeEnter = useCallback(() => {
        if (enterCompletedRef.current) return;
        enterCompletedRef.current = true;
        onEnterCompleteRef.current?.();
    }, []);

    useLayoutEffect(() => {
        if (guardNeverOpened && !hasBeenOpenRef.current && !isOpen) return undefined;
        hasBeenOpenRef.current = isOpen;

        exitCompletedRef.current = isOpen;
        enterCompletedRef.current = !isOpen || skipInitialEnter;
        let thirdFrame = 0;
        let secondFrame = 0;
        let exitFallback = 0;
        let enterFallback = 0;
        const firstFrame = requestAnimationFrame(() => {
            secondFrame = requestAnimationFrame(() => {
                thirdFrame = requestAnimationFrame(() => {
                    setIsShown(isOpen);
                    if (isOpen) {
                        setHasEntered(true);
                        enterFallback = window.setTimeout(completeEnter, fallbackMs);
                        return;
                    }
                    exitFallback = window.setTimeout(completeExit, fallbackMs);
                });
            });
        });

        return () => {
            cancelAnimationFrame(firstFrame);
            cancelAnimationFrame(secondFrame);
            cancelAnimationFrame(thirdFrame);
            window.clearTimeout(exitFallback);
            window.clearTimeout(enterFallback);
            window.clearTimeout(exitHandoffTimeoutRef.current);
        };
    }, [completeEnter, completeExit, fallbackMs, guardNeverOpened, isOpen, skipInitialEnter]);

    const handleTransitionEnd = useCallback(
        (event: React.TransitionEvent<Element>) => {
            if (event.propertyName !== transitionProperty) return;
            if (requireOwnTarget && event.target !== event.currentTarget) return;
            if (isOpen) {
                completeEnter();
                return;
            }
            completeExit();
        },
        [completeEnter, completeExit, isOpen, requireOwnTarget, transitionProperty],
    );

    return { isShown, hasEntered, handleTransitionEnd };
};
